import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { cn } from '@/styles/tailwind-utils';
import { AccountLayout } from '@components/layout/AccountLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { selectUser, useAuthStore } from '@features/auth';
import { mapStaffError, useStaff } from '@features/staff';
import type { StaffMember, StaffRole, StaffStatus } from '@features/staff';

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const STAFF_VIEW_TABS = ['general', 'workingHours', 'settings'] as const;
type StaffViewTab = (typeof STAFF_VIEW_TABS)[number];

const ROLES: StaffRole[] = ['ADMIN', 'PRACTITIONER', 'FRONTDESK'];

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? 'bg-gray-500';
}

function HeaderAvatar({ member }: { member: StaffMember }) {
  const initials = `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
  const color = avatarColor(`${member.firstName}${member.lastName}`);
  const photoUrl = member.practitionerProfile?.profilePhotoUrl;

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={`${member.firstName} ${member.lastName}`}
        className="h-14 w-14 shrink-0 rounded-full object-cover"
      />
    );
  }

  return (
    <div
      className={cn(
        'flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-base font-semibold text-white',
        color,
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// ─── Badges ───────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<StaffStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  INACTIVE: 'bg-gray-100 text-gray-600 ring-gray-500/20',
  INVITED: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  ARCHIVED: 'bg-red-50 text-red-600 ring-red-600/20',
};

const ROLE_STYLES: Record<StaffRole, string> = {
  ADMIN: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  PRACTITIONER: 'bg-teal-50 text-teal-700 ring-teal-600/20',
  FRONTDESK: 'bg-amber-50 text-amber-700 ring-amber-600/20',
};

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 10L6 7l3-3" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.01 2.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

// ─── Sub-header ───────────────────────────────────────────────────────────────

function StaffSubHeader({ member }: { member: StaffMember }) {
  const { t } = useTranslation();
  const displayName = [member.title, member.firstName, member.lastName].filter(Boolean).join(' ');

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 md:px-6">
      {/* Back link */}
      <Link
        to="/account/staff"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeftIcon />
        {t('staff.view.backToStaff')}
      </Link>

      {/* Profile row */}
      <div className="flex items-center gap-4">
        <HeaderAvatar member={member} />

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h1 className="font-display text-xl font-semibold text-foreground">{displayName}</h1>
            {member.isOwner && (
              <span className="text-sm text-muted-foreground">({t('staff.owner')})</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {member.phone && (
              <span className="flex items-center gap-1.5">
                <PhoneIcon />
                {member.phone}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <EmailIcon />
              {member.email}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {member.roles.map((role) => (
              <Badge key={role} className={ROLE_STYLES[role]}>
                {t(`staff.roles.${role}`)}
              </Badge>
            ))}
            <Badge className={STATUS_STYLES[member.status]}>
              {t(`staff.status.${member.status}`)}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── General tab ──────────────────────────────────────────────────────────────

function GeneralTab({ member }: { member: StaffMember }) {
  const { t } = useTranslation();
  const { update, isUpdating, updateError, resetUpdateError } = useStaff();

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().max(20, t('staff.editModal.titleMax')).optional(),
        firstName: z
          .string()
          .min(1, t('staff.editModal.firstNameRequired'))
          .max(100, t('staff.editModal.firstNameMax')),
        lastName: z
          .string()
          .min(1, t('staff.editModal.lastNameRequired'))
          .max(100, t('staff.editModal.lastNameMax')),
        phone: z.string().optional(),
        roles: z
          .array(z.enum(['ADMIN', 'PRACTITIONER', 'FRONTDESK']))
          .min(1, t('staff.editModal.rolesRequired')),
        licenseNumber: z.string().max(100).optional(),
        bio: z.string().max(1000).optional(),
        calendarColor: z
          .string()
          .regex(/^$|^#[0-9A-Fa-f]{6}$/, t('staff.editModal.invalidColor'))
          .optional(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const defaults = useMemo<FormValues>(
    () => ({
      title: member.title ?? '',
      firstName: member.firstName,
      lastName: member.lastName,
      phone: member.phone ?? '',
      roles: member.roles,
      licenseNumber: member.practitionerProfile?.licenseNumber ?? '',
      bio: member.practitionerProfile?.bio ?? '',
      calendarColor: member.practitionerProfile?.calendarColor ?? '',
    }),
    [member],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  // Load the member's values once per member. Keying on the id (rather than the
  // object reference or unstable callbacks) prevents background refetches or
  // incidental re-renders from wiping fields the user is currently typing into.
  useEffect(() => {
    reset(defaults);
    resetUpdateError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.staffId]);

  const selectedRoles = useWatch({ control, name: 'roles' }) ?? [];
  const isPractitioner = selectedRoles.includes('PRACTITIONER');

  // The owner's record is locked for everyone except the owner editing their own profile.
  const currentUser = useAuthStore(selectUser);
  const isSelf =
    !!currentUser &&
    (currentUser.id === member.cognitoSub ||
      currentUser.email.toLowerCase() === member.email.toLowerCase());
  const readOnly = member.isOwner && !isSelf;

  function onSubmit(values: FormValues) {
    if (readOnly) return;

    const hasPractitioner = values.roles.includes('PRACTITIONER');

    update(
      {
        staffId: member.staffId,
        body: {
          title: values.title || undefined,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone || undefined,
          roles: values.roles,
          practitionerProfile: hasPractitioner
            ? {
                licenseNumber: values.licenseNumber || undefined,
                bio: values.bio || undefined,
                calendarColor: values.calendarColor || undefined,
                profilePhotoUrl: member.practitionerProfile?.profilePhotoUrl,
              }
            : null,
        },
      },
      // Clear the dirty state so Save disables again until the next edit.
      { onSuccess: () => reset(values) },
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
      {updateError && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {mapStaffError(updateError, t, 'staff.errors.updateFailed')}
        </div>
      )}

      {readOnly && (
        <p className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          {t('staff.view.ownerReadOnly')}
        </p>
      )}

      {/* Identity */}
      <div className="space-y-4 rounded-md border border-border bg-white p-5 shadow-sm">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('staff.view.generalTitle')}
        </h2>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="sm:w-24 sm:shrink-0">
            <Input
              label={t('staff.editModal.titleLabel')}
              placeholder={t('staff.editModal.titlePlaceholder')}
              error={errors.title?.message}
              disabled={readOnly}
              {...register('title')}
            />
          </div>
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="flex-1">
              <Input
                label={t('staff.editModal.firstNameLabel')}
                error={errors.firstName?.message}
                disabled={readOnly}
                {...register('firstName')}
              />
            </div>
            <div className="flex-1">
              <Input
                label={t('staff.editModal.lastNameLabel')}
                error={errors.lastName?.message}
                disabled={readOnly}
                {...register('lastName')}
              />
            </div>
          </div>
        </div>

        <Input
          label={t('staff.editModal.phoneLabel')}
          type="tel"
          disabled={readOnly}
          {...register('phone')}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">
            {t('staff.editModal.rolesLabel')}
          </span>
          <div className="flex flex-wrap gap-4">
            {ROLES.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  value={role}
                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                  disabled={readOnly}
                  {...register('roles')}
                />
                {t(`staff.roles.${role}`)}
              </label>
            ))}
          </div>
          {errors.roles && <p className="text-xs text-destructive">{errors.roles.message}</p>}
        </div>
      </div>

      {/* Practitioner profile */}
      {isPractitioner && (
        <div className="space-y-3 rounded-md border border-border bg-white p-5 shadow-sm">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('staff.editModal.practitionerProfile')}
          </h2>

          <Input
            label={t('staff.editModal.licenseNumberLabel')}
            error={errors.licenseNumber?.message}
            disabled={readOnly}
            {...register('licenseNumber')}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {t('staff.editModal.bioLabel')}
            </label>
            <textarea
              rows={3}
              className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60"
              disabled={readOnly}
              {...register('bio')}
            />
            {errors.bio && <p className="text-xs text-destructive">{errors.bio.message}</p>}
          </div>

          <Input
            label={t('staff.editModal.calendarColorLabel')}
            placeholder="#4A90D9"
            error={errors.calendarColor?.message}
            disabled={readOnly}
            {...register('calendarColor')}
          />
        </div>
      )}

      {!readOnly && (
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => reset(defaults)}
            disabled={isUpdating || !isDirty}
          >
            {t('staff.view.cancel')}
          </Button>
          <Button type="submit" isLoading={isUpdating} disabled={!isDirty}>
            {t('staff.view.save')}
          </Button>
        </div>
      )}
    </form>
  );
}

// ─── Placeholder tabs ───────────────────────────────────────────────────────

function ComingSoon({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-border bg-white px-5 py-16 text-center shadow-sm">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ─── Content ──────────────────────────────────────────────────────────────────

function StaffMemberContent({ member }: { member: StaffMember }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<StaffViewTab>('general');

  return (
    <>
      {/* Header + tabs share a single white surface */}
      <div className="border-b border-border bg-white shadow-sm">
        <StaffSubHeader member={member} />

        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <nav
            role="tablist"
            aria-label={t('staff.view.tabsLabel')}
            className="-mb-px flex gap-1 overflow-x-auto"
          >
            {STAFF_VIEW_TABS.map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'whitespace-nowrap border-b-2 px-3.5 py-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`staff.view.tabs.${tab}`)}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab panel */}
      <div className="mx-auto max-w-4xl px-4 py-8 md:px-6">
        <div role="tabpanel" aria-label={t(`staff.view.tabs.${activeTab}`)} className="min-h-[40vh]">
          {activeTab === 'general' && <GeneralTab member={member} />}
          {activeTab === 'workingHours' && (
            <ComingSoon message={t('staff.view.workingHoursComingSoon')} />
          )}
          {activeTab === 'settings' && <ComingSoon message={t('staff.view.settingsComingSoon')} />}
        </div>
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StaffMemberViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { staffId } = useParams<{ staffId: string }>();
  const { staff, isLoading, fetchError } = useStaff();

  const member = staff.find((m) => m.staffId === staffId);

  return (
    <AccountLayout>
      {isLoading ? (
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
          <BrandLoader title={t('staff.loading.title')} subtitle={t('staff.loading.subtitle')} />
        </div>
      ) : fetchError ? (
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6">
          <div role="alert" className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
            {mapStaffError(fetchError, t, 'staff.errors.loadFailed')}
          </div>
        </div>
      ) : member ? (
        <StaffMemberContent member={member} />
      ) : (
        <div className="mx-auto max-w-4xl px-4 py-16 text-center md:px-6">
          <p className="text-sm font-medium text-foreground">{t('staff.view.notFound')}</p>
          <button
            type="button"
            onClick={() => navigate('/account/staff')}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ChevronLeftIcon />
            {t('staff.view.backToStaff')}
          </button>
        </div>
      )}
    </AccountLayout>
  );
}
